import { useState } from "react";
import { useNavigate } from "react-router-dom";
import BrandMark from "../components/BrandMark";
import LanguageSwitcher from "../components/LanguageSwitcher";
import PasswordInput from "../components/PasswordInput";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { useI18n } from "../i18n/LanguageContext";

/**
 * Forced password change after the user logged in with an admin-issued
 * temporary password. The "current password" input is the temp one — once
 * the new password is set, the JWT is invalidated server-side and the user
 * is redirected to /login for a fresh session.
 */
export default function ChangePasswordPage() {
  const { user, signOutLocal } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const mandatory = !!user?.must_change_password;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) {
      setError(t("change.mismatch"));
      return;
    }
    if (next === current) {
      setError(t("change.same_as_current"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      // Server cleared cookies + invalidated JWT — log out locally and route to login.
      if (typeof signOutLocal === "function") signOutLocal();
      navigate("/login?reset=ok", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message || t("change.error_generic"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen br-aurora flex flex-col items-center justify-center px-6 py-12">
      <div className="absolute top-5 right-6">
        <LanguageSwitcher />
      </div>
      <div
        className="max-w-[480px] w-full br-card px-8 py-10 br-fade-up"
        data-testid="change-password-card"
      >
        <div className="flex items-center gap-4 mb-6">
          <BrandMark size={44} />
          <div>
            <div className="br-overline">{t("change.overline")}</div>
            <div className="font-serif-display italic text-[1.2rem] text-[color:var(--br-parchment)] mt-1 leading-none">
              {t("change.title_a")}{" "}
              <span className="text-[color:var(--br-bronze-soft)]">{t("change.title_em")}</span>
            </div>
          </div>
        </div>

        <h2 className="font-serif-display text-[1.65rem] leading-tight">
          {mandatory ? t("change.heading_mandatory") : t("change.heading_regular")}
        </h2>
        <p
          className="mt-3 text-[0.92rem] text-[color:var(--br-text-dim)] leading-relaxed"
          data-testid="change-password-intro"
        >
          {mandatory ? t("change.intro_mandatory") : t("change.intro_regular")}
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="change-password-form">
          <label className="block">
            <span className="br-overline">{t("change.current_label")}</span>
            <div className="mt-2">
              <PasswordInput
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="change-password-current-input"
              />
            </div>
          </label>
          <label className="block">
            <span className="br-overline">{t("change.new_label")}</span>
            <div className="mt-2">
              <PasswordInput
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                autoComplete="new-password"
                minLength={10}
                data-testid="change-password-new-input"
              />
            </div>
          </label>
          <label className="block">
            <span className="br-overline">{t("change.confirm_label")}</span>
            <div className="mt-2">
              <PasswordInput
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                autoComplete="new-password"
                minLength={10}
                data-testid="change-password-confirm-input"
              />
            </div>
          </label>
          <p className="text-[0.72rem] text-[color:var(--br-text-dim)] leading-relaxed">
            {t("change.policy_hint")}
          </p>
          {error && (
            <div
              className="text-[0.85rem] py-2 px-3 border-l-2"
              style={{ borderColor: "var(--br-red)", color: "var(--br-red)" }}
              data-testid="change-password-error"
            >
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting || !current || !next || !confirm}
            className="br-btn-primary w-full"
            data-testid="change-password-submit"
          >
            {submitting ? t("change.submitting") : t("change.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}
